import { useMemo } from 'react';
import { GeoJSON } from 'react-leaflet';
import type { FeatureCollection, Feature } from 'geojson';
import { useQuery } from '@tanstack/react-query';
import { queries, topoIstat, topoProvAcr } from '@/data';
import { activeMapCountryConfig } from '@/data/active-country';
import { mapLevel } from '@/features/map-core/countries';
import { useSelectedSets } from './store';

// SelectionOverlay is purely decorative — all pointer events must pass through
// to ChoroplethLayer below so saved communes get the same click/hover behaviour
// as unsaved ones. We use Leaflet's `interactive: false` option on each
// GeoJSON, which sets pointer-events="none" on every SVG path Leaflet renders,
// including MultiPolygon sub-paths (the option propagates through GeoJSON's
// internal addData → geometryToLayer pipeline to every leaf path).

const STYLE_REGION = {
  fillColor: '#2563eb',
  fillOpacity: 0.04,
  color: '#2563eb',
  weight: 1.5,
  opacity: 0.6,
};
const STYLE_PROVINCE = {
  fillColor: '#2563eb',
  fillOpacity: 0.07,
  color: '#1d4ed8',
  weight: 1.5,
  opacity: 0.7,
};
const STYLE_COMUNE = {
  fillColor: '#fbbf24',
  fillOpacity: 0.4,
  color: '#d97706',
  weight: 1,
  opacity: 0.9,
};

function filterByProperty(
  topo: FeatureCollection | undefined,
  predicate: (props: Record<string, unknown>) => boolean,
): FeatureCollection | null {
  if (!topo) return null;
  const features = topo.features.filter((f) => predicate((f.properties ?? {}) as Record<string, unknown>));
  if (features.length === 0) return null;
  return { type: 'FeatureCollection', features };
}

export function SelectionOverlay() {
  const sets = useSelectedSets();
  const { data: meta } = useQuery(queries.meta());
  const version = meta?.version ?? '';

  const { data: regionsTopo } = useQuery({
    ...queries.topo('regions', version),
    enabled: !!version && sets.regions.size > 0,
  });
  const { data: provincesTopo } = useQuery({
    ...queries.topo('provinces', version),
    enabled: !!version && sets.provinces.size > 0,
  });
  const { data: comuniTopo } = useQuery({
    ...queries.topo('comuni', version),
    enabled: !!version && sets.comuni.size > 0,
  });

  // Имена свойств кодов — из реестра страны (см. MapAdminLevel.codeProp).
  const cfg = activeMapCountryConfig();
  const provProp = mapLevel(cfg, 'provinces')?.codeProp;
  const unitProp = mapLevel(cfg, 'comuni')?.codeProp;

  const regionFeatures = useMemo(
    () =>
      filterByProperty(regionsTopo, (p) => {
        const code = p.reg_istat_code_num;
        return typeof code === 'number' && sets.regions.has(String(code));
      }),
    [regionsTopo, sets.regions],
  );

  const provinceFeatures = useMemo(
    () =>
      filterByProperty(provincesTopo, (p) => {
        const acr = provProp ? topoProvAcr(p, provProp) : null;
        return acr !== null && sets.provinces.has(acr);
      }),
    [provincesTopo, sets.provinces, provProp],
  );

  const comuneFeatures = useMemo(
    () =>
      filterByProperty(comuniTopo, (p) => {
        const istat = unitProp ? topoIstat(p, unitProp) : null;
        return istat !== null && sets.comuni.has(istat);
      }),
    [comuniTopo, sets.comuni, unitProp],
  );

  return (
    <>
      {regionFeatures && (
        <GeoJSON
          key={`r-${sets.regions.size}`}
          data={regionFeatures}
          style={STYLE_REGION}
          pane="selectionPane"
          interactive={false}
        />
      )}
      {provinceFeatures && (
        <GeoJSON
          key={`p-${sets.provinces.size}`}
          data={provinceFeatures}
          style={STYLE_PROVINCE}
          pane="selectionPane"
          interactive={false}
        />
      )}
      {comuneFeatures && (
        <GeoJSON
          key={`c-${sets.comuni.size}`}
          data={comuneFeatures as FeatureCollection<Feature['geometry']>}
          style={STYLE_COMUNE}
          pane="selectionPane"
          interactive={false}
        />
      )}
    </>
  );
}
